import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../../app/appTypes"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http"
import { DocumentWorkflowStageTypeEnum } from "../../documents/workflow/documentWorkflowApi"

export interface DocumentWorkflowDefinitionScopeDetailsDto {
    id: number
    organizationNodeId: number | null
    documentType: string
    isDefault: boolean
}

export interface DocumentWorkflowDefinitionStageDetailsDto {
    id: number
    name: string
    description: string
    type: string
    order: number
    defaultDeadlineDays: number | null
}

export interface DocumentWorkflowDefinitionDetailsDto {
    id: number
    name: string
    description: string | null
    isActive: boolean
    versionId: number
    versionNumber: number
    changeDescription: string | null
    scope: DocumentWorkflowDefinitionScopeDetailsDto
    stages: DocumentWorkflowDefinitionStageDetailsDto[]
}

export interface CreateDocumentWorkflowDefinitionDto {
    name: string
    description: string | null
    changeDescription?: string | null
    scopeId?: number | null
    organizationNodeId?: string | null
    documentType?: string | null
    isDefault?: boolean
}

export interface AddDocumentWorkflowDefinitionStageDto {
    name: string
    description: string | null
    type: DocumentWorkflowStageTypeEnum
    order: number
    defaultDeadlineDays?: number | null
}

export interface UpdateDocumentWorkflowDefinitionStageDto {
    name: string
    description: string | null
    type: DocumentWorkflowStageTypeEnum
    order: number
    defaultDeadlineDays?: number | null
}

export const loadCurrentCompanyWorkflowDefinition = createAsyncThunk<
    DocumentWorkflowDefinitionDetailsDto,
    { definitionId: number },
    AppThunkAPIType
>(
    "company-document-workflow/load-current-definition",
    async ({ definitionId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/document-workflow-definitions/${definitionId}/current`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentWorkflowDefinitionDetailsDto
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const loadCompanyWorkflowDefinitionsForOrganizationNode = createAsyncThunk<
    DocumentWorkflowDefinitionDetailsDto[],
    { organizationNodeId: string; documentType?: string | null },
    AppThunkAPIType
>(
    "company-document-workflow/load-definitions-for-organization-node",
    async ({ organizationNodeId, documentType }, { dispatch, rejectWithValue }) => {
        const params = new URLSearchParams()
        if (documentType) {
            params.set("documentType", documentType)
        }

        const query = params.toString()
        const result = await dispatch(getWithAuth({
            url: `api/document-workflow-definitions/organization-nodes/${encodeURIComponent(organizationNodeId)}${query ? `?${query}` : ""}`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentWorkflowDefinitionDetailsDto[]
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const createCompanyWorkflowDefinition = createAsyncThunk<
    DocumentWorkflowDefinitionDetailsDto,
    { dto: CreateDocumentWorkflowDefinitionDto },
    AppThunkAPIType
>(
    "company-document-workflow/create-definition",
    async ({ dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: "api/document-workflow-definitions",
            payload: dto,
        }))

        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentWorkflowDefinitionDetailsDto
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateCompanyWorkflowDefinition = createAsyncThunk<
    DocumentWorkflowDefinitionDetailsDto,
    { definitionId: number; dto: CreateDocumentWorkflowDefinitionDto },
    AppThunkAPIType
>(
    "company-document-workflow/update-definition",
    async ({ definitionId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/document-workflow-definitions/${definitionId}`,
            payload: dto,
        }))

        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentWorkflowDefinitionDetailsDto
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const addCompanyWorkflowDefinitionStage = createAsyncThunk<
    DocumentWorkflowDefinitionDetailsDto,
    { definitionId: number; versionId: number; dto: AddDocumentWorkflowDefinitionStageDto },
    AppThunkAPIType
>(
    "company-document-workflow/add-stage",
    async ({ definitionId, versionId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document-workflow-definitions/${definitionId}/versions/${versionId}/stages`,
            payload: dto,
        }))

        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentWorkflowDefinitionDetailsDto
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateCompanyWorkflowDefinitionStage = createAsyncThunk<
    DocumentWorkflowDefinitionDetailsDto,
    { definitionId: number; versionId: number; stageId: number; dto: UpdateDocumentWorkflowDefinitionStageDto },
    AppThunkAPIType
>(
    "company-document-workflow/update-stage",
    async ({ definitionId, versionId, stageId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/document-workflow-definitions/${definitionId}/versions/${versionId}/stages/${stageId}`,
            payload: dto,
        }))

        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentWorkflowDefinitionDetailsDto
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const deleteCompanyWorkflowDefinitionStage = createAsyncThunk<
    void,
    { definitionId: number; versionId: number; stageId: number },
    AppThunkAPIType
>(
    "company-document-workflow/delete-stage",
    async ({ definitionId, versionId, stageId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({
            url: `api/document-workflow-definitions/${definitionId}/versions/${versionId}/stages/${stageId}`,
        }))

        const { payload } = result
        if (deleteWithAuth.fulfilled.match(result)) {
            return
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)
